import { ColorPartial, PaletteColor } from "@mui/material/styles/createPalette";
import { createTheme } from "components";
import { paletteLightTheme } from "./ColorTheme";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    lightBg: true;
    secondaryDark: true;
    landingBtn: true;
    link: true;
  }
  interface ButtonPropsSizeOverrides {
    extraSmall: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    dark: true;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    primaryDark: true;
    primaryLight: true;
  }
}
declare module "@mui/material/Accordion" {
  interface AccordianPropsVariantOverrides {
    dark: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    bgColor: PaletteColor & ColorPartial;
    light: PaletteColor & ColorPartial;
    dark: PaletteColor & ColorPartial;
    border: PaletteColor & ColorPartial;
    neutral: PaletteColor & ColorPartial;
    secondaryText: PaletteColor & ColorPartial;
    gGrey?: PaletteColor & ColorPartial;
    greyBg?: PaletteColor & ColorPartial;
    neutralBg?: PaletteColor & ColorPartial;
    primaryDark?: PaletteColor & ColorPartial;
    orange?: PaletteColor & ColorPartial;
  }
  interface PaletteOptions {
    bgColor: PaletteColor & ColorPartial;
    dark: PaletteColor & ColorPartial;
    border: PaletteColor & ColorPartial;
    light: PaletteColor & ColorPartial;
    neutral: PaletteColor & ColorPartial;
    secondaryText: PaletteColor & ColorPartial;
    gGrey?: PaletteColor & ColorPartial;
    greyBg?: PaletteColor & ColorPartial;
    neutralBg?: PaletteColor & ColorPartial;
    primaryDark?: PaletteColor & ColorPartial;
    orange?: PaletteColor & ColorPartial;
  }
}

export const ThemeContainer = () => {
  const theme = createTheme({
    palette: paletteLightTheme.palette,
    typography: {
      fontFamily: `Inter`,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Inter';
          }
        `,
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: "0 !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            fontFamily: "Manrope",
            width: 32,
            height: 20,
            padding: 0,
            marginRight: 10,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: 2,
              transitionDuration: "300ms",
              "&.Mui-checked": {
                transform: "translateX(12px)",
                color: paletteLightTheme.palette.light?.main,
                "& + .MuiSwitch-track": {
                  backgroundColor: paletteLightTheme.palette.primary.main,
                  opacity: 1,
                  border: 0,
                },
                "&.Mui-disabled + .MuiSwitch-track": {
                  opacity: 0.5,
                  background: `${paletteLightTheme.palette.dark.main}44`,
                },
              },
              "&.Mui-focusVisible .MuiSwitch-thumb": {
                color: paletteLightTheme.palette.primary.main,
                border: `6px solid ${paletteLightTheme.palette.border.main}`,
              },
              "&.Mui-disabled .MuiSwitch-thumb": {
                color: paletteLightTheme.palette.grey[600],
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: paletteLightTheme.palette.mode === "light" ? 0.7 : 0.3,
              },
            },
            "& .MuiSwitch-thumb": {
              boxSizing: "border-box",
              width: 16,
              height: 16,
            },
            "& .MuiSwitch-track": {
              borderRadius: 26 / 2,
              backgroundColor: paletteLightTheme.palette.grey[300],
              opacity: 1,
              transition: paletteLightTheme.transitions.create(
                ["background-color"],
                {
                  duration: 500,
                },
              ),
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }: any) => {
            return {
              borderRadius: 6,
              boxShadow: "none",
              textTransform: "capitalize",
              lineHeight: "normal",
              minHeight: "40px",
              padding: "0 16px",
              ...(ownerState.variant === "lightBg" && {
                border: "none",
                background: `${theme.palette[ownerState.color].main}11`,
                color: theme.palette[ownerState.color].main,
                "&:hover": {
                  background: `${theme.palette[ownerState.color].main}22`,
                },
              }),
              ...(ownerState.variant === "outlined" && {
                background: "var(--white)",
              }),
              ...(ownerState.size === "extraSmall" && {
                padding: "4px 8px !important",
                lineHeight: "normal",
                minWidth: "10px",
              }),
              ...(ownerState.variant === "link" && {
                padding: 0,
                textDecoration: "underline",
                color: theme.palette[ownerState.color].main,
                minWidth: 0,
                "&:hover": {
                  background: "transparent",
                  textDecoration: "underline",
                },
              }),
              ...(ownerState.variant === "outlined" &&
                ownerState.color === "dark" && {
                  border: "1px solid ",
                  borderColor: "var(--neutral-100)",
                  color: "var(--black)",
                }),
            };
          },
        },
        variants: [
          {
            props: {
              size: "large",
              variant: "outlined",
              className: "extra-large",
            },
            style: {
              padding: "14px 16px",
            },
          },
          {
            props: {
              variant: "landingBtn",
            },
            style: {
              padding: "14px 22px 14px 22px",
              background: `linear-gradient(89.61deg, #006580 0.21%, #258382 108.43%, #489F84 210.75%)`,
              transition: "all 0.3s ease-in-out",
              border: "1px solid #FFFFFF33",
              fontSize: " 15px",
              fontWeight: " 500",
              lineHeight: " 18.15px",
              letterSpacing: " -0.01em",
              color: "#FFFFFF",
              "&:hover": {
                background: `linear-gradient(89.09deg, #006580 -102.26%, #258382 4.16%, #489F84 104.77%)`,
                transition: "all 0.3s ease-in-out",
              },
            },
          },
          {
            props: {
              variant: "contained",
              className: "light",
            },
            style: {
              background: `${paletteLightTheme.palette.border.main}33`,
              // padding: "8px 10px",
              borderRadius: 6,
              color: paletteLightTheme.palette.primary.main,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                background: `${paletteLightTheme.palette.border.main}22`,
              },
            },
          },
        ],
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: paletteLightTheme.palette.border.main,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            backgroundColor: `${paletteLightTheme?.palette?.primary?.main} !important`,
            color: `${paletteLightTheme?.palette?.light?.main}`,
            fontWeight: 700,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: "solid 1px",
            borderColor: paletteLightTheme.palette.neutral[100],
            borderRadius: "10px",
            padding: "24px",
            boxShadow: "none",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-root ": {
              fontWeight: 700,
              background: "#EEF5FA",
              fontSize: 12,
              textTransform: "uppercase",
              color: paletteLightTheme.palette.dark.main,
              "& .MuiTableSortLabel-root": {
                fontWeight: 700,
                background: "#EEF5FA",
                fontSize: 12,
                textTransform: "uppercase",
                color: paletteLightTheme.palette.dark.main,
              },
            },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            return {
              alignItems: "center",
              fontSize: "12px",
              padding: "8px",
              fontWeight: 400,
              borderRadius: 10,
              color: paletteLightTheme.palette[ownerState?.severity].main,
              background: `${theme?.palette[ownerState?.severity]?.main}11 `,
              border: `1px solid ${theme?.palette[ownerState?.severity]?.main}`,
              "& .MuiAlert-icon": {
                padding: 0,
                marginRight: "6px",
              },
              "& .MuiAlert-message": {
                padding: 0,
              },
            };
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: "solid 1px",
            borderColor: paletteLightTheme.palette.border.main,
            borderRadius: "10px",
            width: `-webkit-fill-available !important`,
            "& .MuiDataGrid-root ": {
              flexDirection: "column",
              borderRadius: "10px",
              background: paletteLightTheme.palette.light?.main,
              fontSize: 14,
              border: "none",
            },
            "& .MuiDataGrid-overlayWrapper": {
              height: "100%",
            },
            "& .MuiDataGrid-overlayWrapper .MuiDataGrid-overlayWrapperInner": {
              height: "100% !important",
            },
            "& .MuiTablePagination-actions .MuiButtonBase-root": {
              padding: 0,
              borderRadius: 6,
            },
            "& .MuiSelect-select.MuiTablePagination-select.MuiSelect-standard.MuiInputBase-input":
              {
                background: `${paletteLightTheme?.palette?.primary?.main}22`,
                borderRadius: 6,
                minWidth: 0,
                padding: "3px 18px 3px 4px",
                color: paletteLightTheme?.palette?.primary?.main,
              },
            "& .MuiPagination-ul": {
              justifyContent: "flex-end",
            },
            "& .MuiDataGrid-cellContent": {},
            "& .MuiPaginationItem-outlined": {
              background: paletteLightTheme.palette.border.main,
              border: "none",
              color: paletteLightTheme.palette.primary.main,
              "& .MuiSvgIcon-root": {
                height: 25,
                width: 25,
              },
            },
            "& .MuiPaginationItem-page": {
              display: "none",
            },
            "& .MuiPaginationItem-ellipsis": {
              display: "none",
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeader ": {
              outline: "none",
              borderTop: `solid 1px ${paletteLightTheme.palette.border.main}44`,
              borderBottom: `solid 1px ${paletteLightTheme.palette.border.main}44`,
              color: paletteLightTheme.palette.dark.main,
              background: "#EEF5FA",
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-columnHeaders": {
              background: "#EEF5FA",
            },
            "& .MuiDataGrid-pinnedColumnHeaders--right": {
              background: "#EEF5FA",
            },
            "& .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeader":
              {
                border: "none",
              },
            "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within ": {
              outline: "none",
            },
            "& .MuiDataGrid-root .MuiDataGrid-toolbarContainer ": {
              marginLeft: "auto",
              gap: 0,
              marginBottom: "4px",
            },
            "& .MuiDataGrid-root .MuiToolbar-root  ": {
              marginLeft: "8px",
              padding: 0,
            },
            "& .MuiDataGrid-footerContainer": {
              display: "none",
              border: "none",
              borderBottom: `1px solid ${paletteLightTheme.palette.border.main}`,
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle ": {
              fontWeight: 600,
              fontSize: 12,
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-columnHeader .MuiButtonBase-root": {
              background: "transparent",
              border: "none",
              padding: 0,
            },
            "& .MuiDataGrid-columnHeader .MuiButtonBase-root:hover": {
              background: "transparent",
              padding: 0,
              color: paletteLightTheme.palette.dark.main,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumns ": {
              backgroundColor: paletteLightTheme.palette.light?.main,
              backgroundImage: "none",
            },
            // ==============================================================================================
            "& .MuiDataGrid-cell": {
              borderRight: `1px solid ${paletteLightTheme.palette.border.main}`,
              borderBottom: `1px solid ${paletteLightTheme.palette.border.main}`,
            },
            "& .MuiDataGrid-columnHeader": {
              borderRight: `1px solid ${paletteLightTheme.palette.border.main}`,
              borderBottom: `1px solid ${paletteLightTheme.palette.border.main}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            whiteSpace: "nowrap",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            "& td,th": {
              padding: "8px",
            },
            "& th": {
              padding: "8px",
              color: paletteLightTheme.palette.secondaryText.main,
              fontWeight: 400,
              fontSize: 12,
            },
            "& tbody > tr:last-child > td": {
              borderBottom: "none",
            },
            "& .MuiTableCell-root": {
              fontSize: 14,
              minHeight: 40,
            },
            "& .MuiTableBody-root tr:nth-of-type(odd)": {
              background: paletteLightTheme.palette.light?.main,
            },
            "& .MuiButtonBase-root.MuiTableSortLabel-root": {
              color: paletteLightTheme.palette.secondaryText.main,
              minHeight: 40,
              fontWeight: 400,
              fontSize: 12,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: () => {
            return { "&:hover": { background: "transparent" } };
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "& .MuiTabs-flexContainer": {
              gap: "16px",
            },
            "& .MuiButtonBase-root": {
              padding: "16px 0",
              fontSize: 14,
            },
          },
        },
        variants: [
          {
            props: { variant: "fullWidth", className: "gridkey" },
            style: {
              background: paletteLightTheme.palette.light?.main,
              borderRadius: "10px",
              minHeight: 40,
              border: "solid 1px",
              borderColor: paletteLightTheme.palette.border.main,
              transition: "all .3s ",
              "& .MuiTabs-indicator": {
                height: 0,
              },
              "& .Mui-selected": {
                minHeight: 40,
                background: `linear-gradient(91.84deg,${paletteLightTheme.palette.primary.main} 0%, ${paletteLightTheme.palette.secondary.main} 100%)`,
                borderRadius: "10px",
                color: `#fff !important`,
              },
              "& button": {
                padding: "8px !important",
                minHeight: 40,
                textTransform: "capitalize !important",
              },
            },
          },
          {
            props: { variant: "standard", className: "gridkey" },
            style: {
              background: paletteLightTheme.palette.light?.main,
              borderRadius: "10px",
              minHeight: 36,
              minWidth: 70,
              border: `solid 1px ${paletteLightTheme.palette.border.main}`,
              transition: "all .3s ",
              "& .MuiTabs-indicator": {
                height: 0,
              },
              "& .Mui-selected": {
                minHeight: 36,
                minWidth: 70,
                background: `linear-gradient(91.84deg,${paletteLightTheme.palette.primary.main} 0%, ${paletteLightTheme.palette.secondary.main} 100%)`,
                borderRadius: "10px",
                color: `#fff !important`,
              },
              "& button": {
                padding: "8px !important",
                minHeight: 36,
                minWidth: 70,
                textTransform: "capitalize !important",
              },
            },
          },
          {
            props: { variant: "standard" },
            style: {
              minHeight: 36,
              "& button": {
                padding: "0 10px",
                minHeight: 36,
                minWidth: `max-content`,
                textTransform: "capitalize !important",
              },
            },
          },
          {
            props: { variant: "standard", className: "light" },
            style: {
              color: "#6B7280",
              fontSize: 14,
              fontWeight: 400,
              minHeight: "0px !important",
              borderRadius: 6,
              padding: 4,
              background: "#F3F4F6",
              "& .MuiButtonBase-root.MuiTab-root": {
                padding: "6px 10px !important",
                borderRadius: 6,
                minHeight: "0px !important",
              },
              "& .MuiTabs-flexContainer ": {
                gap: "6px !important",
              },
              "& .MuiButtonBase-root.Mui-selected": {
                color: paletteLightTheme.palette.dark.main,
                background: "var(--white)",
              },
            },
          },
          {
            props: { variant: "scrollable" },
            style: {
              minHeight: 36,
              "& button": {
                padding: "0 10px",
                minHeight: 36,
                minWidth: `max-content`,
                textTransform: "capitalize !important",
              },
            },
          },
        ],
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ ownerState }) => {
            return {
              margin: "0 !important",
              ...(ownerState.variant === "elevation" && {
                border: "1px solid",
                borderColor: paletteLightTheme.palette.border.main,
                borderRadius: "8px !important",
              }),
            };
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: "primaryDark" },
            style: {
              color: paletteLightTheme.palette.dark.main,
              fontSize: 14,
              fontWeight: 700,
            },
          },
          {
            props: { variant: "primaryLight" },
            style: {
              color: "#697586",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
            },
          },
        ],
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: 16,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ ownerState }) => ({
            ...(ownerState?.placement === "top" && {
              marginBottom: "0px !important",
            }),
            padding: "8px 12px",
            borderRadius: "8px",
            background: "var(--neutral-900)",
            "& .MuiTooltip-arrow": {
              color: "var(--neutral-900)",
            },
          }),
        },
      },
    },
  });

  return { theme };
};
